import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Alert, AlertTitle, Box, CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useMemo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { uiLocaleInfo } from '@dubforge/shared';
import { ApiError } from './api/client';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './components/Toast';
import { loadConfig } from './config';
import { i18n, useLocale } from './i18n';
import { createAppTheme } from './theme/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Don't hammer the API on permission or not-found errors.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

// Separate style caches so right-to-left languages get mirrored CSS (margins, padding, alignment).
const caches = {
  ltr: createCache({ key: 'mui' }),
  rtl: createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] }),
};

/** Theme and style cache that follow the interface language's writing direction. */
function Themed({ children }: { children: ReactNode }) {
  const dir = uiLocaleInfo(useLocale()).dir;
  const theme = useMemo(() => createAppTheme(dir), [dir]);
  return (
    <CacheProvider value={caches[dir]}>
      <ThemeProvider theme={theme} defaultMode="system">
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}

function StartupError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 3, maxWidth: 640, mx: 'auto', mt: 8 }}>
      <Alert severity="error">
        <AlertTitle>{t('app.startupFailed')}</AlertTitle>
        {error instanceof Error ? error.message : String(error)}. For local development, set VITE_DEV_BACKEND
        in frontend/.env.local to your deployed dev URL.
      </Alert>
    </Box>
  );
}

const root = createRoot(document.getElementById('root')!);
const render = (children: ReactNode) =>
  root.render(
    <StrictMode>
      <Themed>{children}</Themed>
    </StrictMode>,
  );

void i18n; // initialised on import, before the first render
loadConfig()
  .then(async () => {
    // Import the app only after Amplify is configured, so it can finish an OAuth callback.
    const { App } = await import('./App');
    render(
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>,
    );
  })
  .catch((err: unknown) => {
    console.error(err);
    render(<StartupError error={err} />);
  });
